import * as React from 'react'

import { RotateCcw } from 'lucide-react'
import { action, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'

import { LocationType } from '~/client/graph'
import { Button } from '~/client/src/shared/components/Button/Button'
import { ListSelector } from '~/client/src/shared/components/ListSelector'
import LocationEquipmentResourcePicker from '~/client/src/shared/components/LocationPicker/LocationEquipmentResourcePicker'
import ResponsiveSurface from '~/client/src/shared/components/ResponsiveSurface/ResponsiveSurface'
import IRestrictionViewModel from '~/client/src/shared/interfaces/IRestrictionViewModel'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import DeliveryRestrictionsStore from '~/client/src/shared/stores/domain/DeliveryRestrictions.store'

interface IProps {
  store: DeliveryRestrictionsStore
  restriction: IRestrictionViewModel
  anchor: HTMLElement | null
}

const allowedIdsOf = (restriction: IRestrictionViewModel): string[] =>
  [
    ...restriction.routes,
    ...restriction.gates,
    ...restriction.equipment,
    ...restriction.levels,
    ...restriction.areas,
    ...restriction.stagings,
    ...restriction.interiorDoors,
    ...restriction.interiorPaths,
  ].map(({ id }) => id)

const restrictedObjectTypes = [
  LocationType.Building,
  LocationType.Zone,
  LocationType.LogisticsObject,
  LocationType.VerticalObject,
  LocationType.ProjectLocation,
]

@observer
export default class RestrictionModal extends React.Component<IProps> {
  // Remounts the picker on Restore defaults, even when the defaults match the seed.
  @observable private resetCount = 0

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  private get seedIds(): string[] {
    const { store, restriction } = this.props
    return this.resetCount
      ? store.getDefaultAllowedIds()
      : allowedIdsOf(restriction)
  }

  public render() {
    const { store, anchor } = this.props
    const title = Localization.translator.selectPermittedResources

    return (
      <ResponsiveSurface
        open={true}
        onOpenChange={open => !open && store.hideRestrictionModal()}
        title={title}
        anchor={anchor}
        sizing="fill"
      >
        <ListSelector.Title
          title={title}
          onClose={store.hideRestrictionModal}
        />
        <div className="tw:flex tw:justify-end tw:px-2.5">
          <Button
            variant="primary-ghost"
            size="sm"
            onClick={this.restoreDefaults}
          >
            <RotateCcw data-icon="inline-start" />
            {Localization.translator.restoreDefaults}
          </Button>
        </div>
        <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col">
          <LocationEquipmentResourcePicker
            key={this.resetCount}
            mode="multi"
            allowEmptySelection={true}
            selectedIds={this.seedIds}
            excludedLocationTypes={restrictedObjectTypes}
            onSelect={this.updateRestriction}
            onClose={store.hideRestrictionModal}
          />
        </div>
      </ResponsiveSurface>
    )
  }

  @action.bound
  private restoreDefaults(): void {
    this.resetCount += 1
  }

  private updateRestriction = (ids: string[]): void => {
    const { store, restriction } = this.props
    void store.updateRestriction(restriction, ids)
  }
}
